// ═══════════════════════════════════════════════════════════════
// StripeAdapter.js — Wraps Cloud Function calls for billing operations
// Adapter pattern: host app provides cloudFunctionUrl, module handles API calls.
// ═══════════════════════════════════════════════════════════════

/**
 * Create a Stripe adapter for billing operations.
 * @param {{ cloudFunctionUrl: string }} config
 * @returns {object} — adapter with async methods
 */
export function createStripeAdapter({ cloudFunctionUrl }) {
  if (!cloudFunctionUrl) {
    throw new Error('StripeAdapter requires cloudFunctionUrl');
  }

  async function post(endpoint, body) {
    const headers = { 'Content-Type': 'application/json' };
    // Attach Firebase auth token if available
    try {
      const token = await window.firebaseAuth?.currentUser?.getIdToken(false);
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (e) { /* proceed without auth */ }
    const response = await fetch(`${cloudFunctionUrl}/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    return response.json();
  }

  return {
    async prepareOrganization({ name, plan, maxSeats }) {
      return post('prepareOrganizationCheckout', { name, plan, maxSeats });
    },
    /**
     * Create a Stripe Checkout Session and return the redirect URL.
     * @param {{ priceKey, userId, authUid?, tenantId?, seats?, successUrl, cancelUrl }} params
     * @returns {{ url?: string, sessionId?: string, error?: string }}
     */
    async createCheckoutSession({ priceKey, userId, authUid, tenantId, seats, successUrl, cancelUrl }) {
      return post('createCheckoutSession', {
        priceKey,
        userId,
        authUid,
        tenantId,
        seats,
        successUrl,
        cancelUrl,
      });
    },

    /**
     * Create a Stripe Customer Portal Session and return the redirect URL.
     * @param {{ userId, tenantId?, returnUrl }} params
     * @returns {{ url?: string, error?: string }}
     */
    async createPortalSession({ userId, tenantId, returnUrl }) {
      return post('createCustomerPortalSession', {
        userId,
        tenantId,
        returnUrl,
      });
    },

    /**
     * Update seat quantity on an org subscription.
     * @param {{ tenantId, newSeatCount }} params
     * @returns {{ success?: boolean, previousSeats?: number, newSeats?: number, activeUsers?: number, error?: string }}
     */
    async manageSeatQuantity({ tenantId, newSeatCount }) {
      return post('manageSeatQuantity', {
        tenantId,
        newSeatCount,
      });
    },

    /**
     * Cancel a tenant (support-initiated).
     * @param {{ tenantId, cancelledBy }} params
     * @returns {{ success?: boolean, gracePeriodEnds?: string, error?: string }}
     */
    async cancelTenant({ tenantId, cancelledBy }) {
      return post('cancelTenant', {
        tenantId,
        cancelledBy,
      });
    },
  };
}
